import React, { useEffect, useRef, useState } from "react";
import { Input } from "rsuite";
import styles from "./PriceComparisonBlock.module.css";
import { usePurchaseOrders } from "../../usePurchaseOrders";
import { parseOrderAmount } from "../../orderAmount";
import { showToast } from "Components/Toast";

// 1 unit of the vendor's currency in USD. USD vendors are rate 1; non-USD
// vendors need a crossRate (set in VendorsBlock) before USD values compute.
const vendorRate = (vendor) => {
  const { currency } = parseOrderAmount(vendor?.orderAmount);
  if (String(currency).toUpperCase() === "USD") return 1;
  return vendor?.crossRate != null ? Number(vendor.crossRate) : null;
};

const vendorCurrency = (vendor) =>
  parseOrderAmount(vendor?.orderAmount).currency || "USD";

// Stable per-column key. Prefer vendorIndex (nanoid), but fall back to the
// Vendor id for legacy vendors added before vendorIndex existed — otherwise
// the column key would be `undefined` and saves fail with a 400.
const columnKey = (vendor) => vendor?.vendorIndex || vendor?.id;

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return isNaN(n) ? null : n;
};

const fmt = (v) =>
  v == null
    ? null
    : v.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

// Clean a stored price string for display: round to <=2 decimals so float
// artifacts (e.g. "2.8000000000000003") show as "2.8", without forcing
// trailing zeros. Leaves non-numeric/empty values as-is.
const clean2 = (v) => {
  const n = toNum(v);
  if (n == null) return v ?? "";
  return String(Math.round(n * 100) / 100);
};

function PriceComparisonBlock({
  items = [],
  vendors = [],
  itemVendorPrices = {},
  poStatus,
}) {
  const { saveVendorItemPrices } = usePurchaseOrders();

  // Columns: non-cancelled vendors, in order. A vendor is compared when it has
  // a quotation file (the default), unless the user explicitly excluded it; a
  // vendor without a file is compared only if explicitly included.
  // includeInComparison is 3-state: true = force include, false = force
  // exclude, undefined = default (by quotation file).
  const isIncludedInComparison = (v) => {
    if (v?.includeInComparison === true) return true;
    if (v?.includeInComparison === false) return false;
    return !!(v?.quotationFiles && v.quotationFiles.length > 0);
  };
  const columns = (vendors || []).filter(
    (v) => v?.status !== "cancelled" && isIncludedInComparison(v),
  );

  // Prices are vendor-comparison data (peer to vendor orderAmount), editable
  // throughout the comparison lifecycle — only locked in terminal states.
  // Mirrors VendorsBlock, which gates its editable fields on `archived`.
  const locked = ["archived", "deleted", "canceled"].includes(poStatus);

  // Local draft of prices keyed itemId -> vendorIndex -> priceString, seeded
  // from props so typing is smooth and a refetch elsewhere re-seeds cleanly.
  const [draft, setDraft] = useState({});
  useEffect(() => {
    const next = {};
    for (const itemId of Object.keys(itemVendorPrices || {})) {
      next[itemId] = {};
      for (const vi of Object.keys(itemVendorPrices[itemId] || {})) {
        next[itemId][vi] = clean2(itemVendorPrices[itemId][vi]?.price);
      }
    }
    setDraft(next);
  }, [itemVendorPrices]);

  // Resizable Item column. Default 275px; the user can drag the header edge to
  // widen it for long item names. Min 150px.
  const [itemColWidth, setItemColWidth] = useState(275);
  const resizeRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startW: itemColWidth };
    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const delta = ev.clientX - resizeRef.current.startX;
      setItemColWidth(Math.max(150, resizeRef.current.startW + delta));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const cellValue = (itemId, vi) => draft?.[itemId]?.[vi] ?? "";

  const setCell = (itemId, vi, value) =>
    setDraft((d) => ({
      ...d,
      [itemId]: { ...(d[itemId] || {}), [vi]: value },
    }));

  // Which cell is currently being edited (click-to-edit). `${itemId}|${vi}`.
  const [editing, setEditing] = useState(null);
  const cellKey = (itemId, vi) => `${itemId}|${vi}`;

  const isUsdVendor = (vendor) =>
    String(vendorCurrency(vendor)).toUpperCase() === "USD";

  // USD value of a cell = entered price * vendor rate (or null if no rate).
  // Zero / blank / negative prices are treated as "no quote" — they never count
  // as the cheapest and are not highlighted.
  const cellUSD = (itemId, vendor) => {
    const price = toNum(cellValue(itemId, columnKey(vendor)));
    if (price == null || price <= 0) return null;
    const rate = vendorRate(vendor);
    return rate == null ? null : price * rate;
  };

  // Lowest USD price per item row (for highlighting the cheapest vendor).
  const lowestUSDForItem = (itemId) => {
    let min = null;
    columns.forEach((v) => {
      const u = cellUSD(itemId, v);
      if (u != null && (min == null || u < min)) min = u;
    });
    return min;
  };

  // Column total in USD (sum of each cell's USD value). Zero/blank/negative
  // prices are ignored. null if no real prices, or if the column has a priced
  // cell the vendor can't convert (non-USD without crossRate).
  const columnTotalUSD = (vendor) => {
    const rate = vendorRate(vendor);
    let total = 0;
    let any = false;
    let convertible = rate != null;
    items.forEach((it) => {
      const price = toNum(cellValue(it.id, columnKey(vendor)));
      if (price == null || price <= 0) return;
      any = true;
      if (rate == null) convertible = false;
      else total += price * rate;
    });
    if (!any) return null;
    return convertible ? total : null;
  };

  // How many items have a price > 0 for this vendor (coverage, e.g. 23/40).
  const columnCoverage = (vendor) => {
    let set = 0;
    items.forEach((it) => {
      const price = toNum(cellValue(it.id, columnKey(vendor)));
      if (price != null && price > 0) set += 1;
    });
    return { set, total: items.length };
  };

  const lowestColumnTotal = (() => {
    let min = null;
    columns.forEach((v) => {
      const t = columnTotalUSD(v);
      if (t != null && (min == null || t < min)) min = t;
    });
    return min;
  })();

  // Persist a single cell on blur/Enter (if changed vs the saved value), then
  // leave edit mode so the cell shows its USD value again.
  const saveCell = (item, vendor) => {
    setEditing(null);
    if (locked) return;
    const value = cellValue(item.id, columnKey(vendor));
    const saved = itemVendorPrices?.[item.id]?.[columnKey(vendor)]?.price ?? "";
    if (String(value).trim() === String(saved).trim()) return;
    saveVendorItemPrices(columnKey(vendor), vendor.id, [
      {
        itemId: item.id,
        price: clean2(String(value).replace(/[^0-9.\-]/g, "")),
        currency: vendorCurrency(vendor),
      },
    ]);
  };

  // Excel-style column paste: a vertical range fills the column from the
  // clicked row down, mapped by row order.
  const onColumnPaste = (e, vendor, startRowIndex) => {
    if (locked) return;
    const text = e.clipboardData.getData("text/plain");
    if (!text) return;
    e.preventDefault();

    const lines = text
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .map((line) => (line.split("\t")[0] || "").trim());
    while (lines.length && lines[lines.length - 1] === "") lines.pop();
    if (!lines.length) return;

    const currency = vendorCurrency(vendor);
    const prices = [];
    let dropped = 0;
    lines.forEach((raw, i) => {
      const target = items[startRowIndex + i];
      if (!target) {
        dropped += 1;
        return;
      }
      const cleaned = clean2(raw.replace(/[^0-9.\-]/g, ""));
      prices.push({ itemId: target.id, price: cleaned, currency });
    });
    if (!prices.length) return;

    // Optimistic local update.
    setDraft((d) => {
      const next = { ...d };
      prices.forEach(({ itemId, price }) => {
        next[itemId] = { ...(next[itemId] || {}), [columnKey(vendor)]: price };
      });
      return next;
    });

    saveVendorItemPrices(columnKey(vendor), vendor.id, prices).then((ok) => {
      if (ok) {
        showToast(
          "s",
          `Pasted ${prices.length} price${prices.length > 1 ? "s" : ""}` +
            (dropped ? ` (${dropped} extra ignored — add more items)` : ""),
        );
      }
    });
  };

  if (!columns.length) return null;

  // grid: row-number | item label (resizable) | one 150px column per vendor
  const gridTemplate = `36px ${itemColWidth}px repeat(${columns.length}, 150px)`;

  return (
    <div className={styles.container}>
      <div className={styles.header_row}>
        <div className={styles.title}>Vendor price comparison</div>
        <div className={styles.hint}>
          Paste a column of prices from Excel into a vendor column. USD values
          use each vendor's cross rate.
        </div>
      </div>

      <div className={styles.scroll}>
        <div
          className={styles.grid}
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {/* Header */}
          <div className={`${styles.cell} ${styles.head}`}>#</div>
          <div className={`${styles.cell} ${styles.head} ${styles.left}`}>
            Item
            <span
              className={styles.resize_handle}
              onMouseDown={startResize}
              title="Drag to resize"
            />
          </div>
          {columns.map((v) => (
            <div
              key={columnKey(v)}
              className={`${styles.cell} ${styles.head}`}
              title={v.name}
            >
              <div className={styles.vendor_name}>{v.name}</div>
              <div className={styles.vendor_ccy}>
                {vendorCurrency(v)}
                {vendorRate(v) == null && (
                  <span className={styles.no_rate}> · no rate</span>
                )}
              </div>
            </div>
          ))}

          {/* Rows */}
          {items.map((item, rowIdx) => {
            const rowLow = lowestUSDForItem(item.id);
            return (
              <React.Fragment key={item.id}>
                <div className={`${styles.cell} ${styles.idx}`}>
                  {rowIdx + 1}
                </div>
                <div
                  className={`${styles.cell} ${styles.left} ${styles.label}`}
                >
                  {item.partName ||
                    item.otherPart ||
                    item.partNo ||
                    "(no part)"}
                </div>
                {columns.map((v) => {
                  const usd = cellUSD(item.id, v);
                  const isLow =
                    usd != null &&
                    rowLow != null &&
                    usd === rowLow &&
                    columns.length > 1;
                  const usdVendor = isUsdVendor(v);
                  const rate = vendorRate(v);
                  const raw = cellValue(item.id, columnKey(v));
                  const isEditing = editing === cellKey(item.id, columnKey(v));

                  // Price not set or zero → gentle-red highlight (no quote).
                  const priceNum = toNum(raw);
                  const isMissing =
                    !isEditing && (priceNum == null || priceNum <= 0);

                  // All vendors: read mode shows the USD price as plain text;
                  // clicking a cell reveals the input. USD vendors edit in USD
                  // directly; non-USD vendors edit in their currency (suffix
                  // shown) and the table displays the converted USD.
                  const showInput = isEditing;

                  return (
                    <div
                      key={columnKey(v)}
                      className={`${styles.cell} ${styles.price_cell} ${
                        isLow ? styles.low : isMissing ? styles.missing : ""
                      }`}
                    >
                      {showInput ? (
                        <div className={styles.input_wrap}>
                          <Input
                            size="sm"
                            disabled={locked}
                            className={styles.price_input}
                            value={raw}
                            placeholder="—"
                            autoFocus={isEditing}
                            onChange={(val) =>
                              setCell(item.id, columnKey(v), val)
                            }
                            onBlur={() => saveCell(item, v)}
                            onPaste={(e) => onColumnPaste(e, v, rowIdx)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                saveCell(item, v);
                              }
                            }}
                          />
                          {!usdVendor && (
                            <span className={styles.ccy_suffix}>
                              {vendorCurrency(v)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div
                          className={styles.usd_value}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (!locked)
                              setEditing(cellKey(item.id, columnKey(v)));
                          }}
                          onPaste={(e) => onColumnPaste(e, v, rowIdx)}
                        >
                          {rate == null
                            ? raw
                              ? "set rate"
                              : "—"
                            : usd != null
                              ? `${fmt(usd)} USD`
                              : "—"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

          {/* Totals row */}
          <div className={`${styles.cell}`}></div>
          <div className={`${styles.cell} ${styles.left}`}>Total (USD)</div>
          {columns.map((v) => {
            const total = columnTotalUSD(v);
            const cov = columnCoverage(v);
            const isLow =
              total != null &&
              lowestColumnTotal != null &&
              total === lowestColumnTotal &&
              columns.length > 1;
            return (
              <div
                key={columnKey(v)}
                className={`${styles.cell} ${styles.total} ${styles.right} ${
                  isLow ? styles.low_total : ""
                }`}
              >
                <span
                  className={`${styles.coverage} ${
                    cov.set < cov.total ? styles.coverage_partial : ""
                  }`}
                  title={`${cov.set} of ${cov.total} items priced`}
                >
                  {cov.set}/{cov.total}
                </span>
                <span>
                  {total != null
                    ? `${fmt(total)} USD`
                    : vendorRate(v) == null
                      ? "set rate"
                      : "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PriceComparisonBlock;
